import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import { BucketAvailabilityRow } from "./utils/downloadBucketAvailabilityPdf";

/** Helvetica mis-draws U+20B9 (₹); Noto Sans renders it correctly in PDF. */
Font.register({
  family: "NotoSans",
  src: "https://fonts.gstatic.com/s/notosans/v36/o-0IIpQlx3QUlC5A4PNb4g.ttf",
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingHorizontal: 40,
    paddingBottom: 36,
    fontSize: 9,
    fontFamily: "Helvetica",
  },

  // HEADER TABLE
  headerTable: {
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 12,
  },

  headerTopRow: {
    flexDirection: "row",
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
  },

  // 🔥 FIXED: removed vertical divider line
  headerLeft: {
    width: "60%",
    padding: 10,
    // ❌ borderRightWidth removed
  },

  headerRight: {
    width: "40%",
    padding: 10,
    justifyContent: "center",
    alignItems: "flex-end",
  },

  logo: {
    width: 60,
    height: 60,
    objectFit: "contain",
  },

  address: {
    fontSize: 8,
    lineHeight: 1.3,
  },

  title: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
  },

  headerBottomRow: {
    flexDirection: "row",
  },

  metaLeft: {
    width: "60%",
    padding: 8,
    borderRightWidth: 0.8,
    borderRightColor: "#000",
  },

  metaRight: {
    width: "40%",
    padding: 8,
  },

  metaLine: {
    fontSize: 9,
    marginBottom: 3,
  },

  metaLabel: {
    fontWeight: "normal",
  },

  metaValue: {
    fontFamily: "Helvetica-Bold",
  },

  /** Bordered box: header + data rows only. Footer sits below so side borders don’t run through MRP row. */
  tableWithFooter: {
    width: "100%",
    marginBottom: 18,
  },

  table: {
    width: "100%",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 0,
    borderColor: "#000",
  },

  /**
   * Same inner width as `table` rows: table has borderLeft/Right 1pt, so body cells are inset.
   * Without this padding, % column widths don’t line up with the table above.
   */
  totalsFooterOuter: {
    width: "100%",
    paddingLeft: 1,
    paddingRight: 1,
  },

  row: {
    flexDirection: "row",
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
  },

  /** Footer row: same layout as data rows; no bottom line (table has no bottom border). */
  totalsFooterRow: {
    flexDirection: "row",
    borderBottomWidth: 0,
  },

  headerRow: {
    flexDirection: "row",
    backgroundColor: "#e5e5e5",
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
  },

  cell: {
    padding: 6,
    borderRightWidth: 0.8,
    borderRightColor: "#000",
    fontSize: 8,
  },

  cellLast: {
    padding: 6,
    fontSize: 8,
  },

  tableHeaderText: {
    fontFamily: "Helvetica-Bold",
  },

  colName: { width: "34%", textAlign: "left" },
  colMrp: { width: "13%", textAlign: "center" },
  colOrdered: { width: "12%", textAlign: "center" },
  colAvail: { width: "20%", textAlign: "center" },
  colDelayed: { width: "21%", textAlign: "center" },

  mrpCellText: {
    fontFamily: "NotoSans",
  },

  emptyNote: {
    fontSize: 8,
    color: "#666",
    marginBottom: 12,
  },
  /** Footer row: no vertical column lines (borderRight removed). */
  totalsFooterCell: {
    padding: 6,
    borderRightWidth: 0,
    fontSize: 8,
  },
  totalsFooterLabel: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },
  /** Match data cells: Available / 7–30 columns use center in colAvail / colDelayed */
  totalsFooterValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },
});

function formatMrp(mrp: number | string | undefined | null): string {
  if (mrp === undefined || mrp === null) return "—";
  const cleanValue = String(mrp).replace(/[^\d.]/g, "");
  if (!cleanValue || Number.isNaN(Number(cleanValue))) return "—";
  return `\u20B9 ${cleanValue}`;
}

function formatAmountValue(value: number): string {
  return Number(value || 0).toFixed(2);
}

export interface BucketAvailabilityPdfProps {
  availableRows: BucketAvailabilityRow[];
  delayedRows: BucketAvailabilityRow[];
  customerName: string;
  documentDate: string;
  orderNumber?: string;
  logoSrc?: string;
}

function CombinedPartsTable({
  rows,
  totalAvailableValue,
  totalAvailableIn7To30DaysValue,
}: {
  rows: BucketAvailabilityRow[];
  totalAvailableValue: number;
  totalAvailableIn7To30DaysValue: number;
}) {
  if (rows.length === 0) {
    return <Text style={styles.emptyNote}>No line items available.</Text>;
  }

  return (
    <View style={styles.tableWithFooter}>
      <View style={styles.table}>
        <View style={styles.headerRow}>
          <Text style={[styles.cell, styles.colName, styles.tableHeaderText]}>
            Part Name
          </Text>
          <Text style={[styles.cell, styles.colMrp, styles.tableHeaderText]}>
            MRP
          </Text>
          <Text style={[styles.cell, styles.colOrdered, styles.tableHeaderText]}>
            Ordered Qty
          </Text>
          <Text style={[styles.cell, styles.colAvail, styles.tableHeaderText]}>
            Available Qty
          </Text>
          <Text
            style={[styles.cellLast, styles.colDelayed, styles.tableHeaderText]}
          >
            Qty Available in 7-30 days
          </Text>
        </View>

        {rows.map((r, i) => (
          <View key={i} style={styles.row}>
            <Text style={[styles.cell, styles.colName]}>{r.partName}</Text>
            <Text style={[styles.cell, styles.colMrp, styles.mrpCellText]}>
              {formatMrp(r.mrp)}
            </Text>
            <Text style={[styles.cell, styles.colOrdered]}>{r.orderedQty}</Text>
            <Text style={[styles.cell, styles.colAvail]}>{r.availableQty}</Text>
            <Text style={[styles.cellLast, styles.colDelayed]}>
              {r.remainingQty}
            </Text>
          </View>
        ))}
      </View>
      <View style={styles.totalsFooterOuter}>
        <View style={styles.totalsFooterRow}>
          <Text style={[styles.totalsFooterCell, styles.colName]}> </Text>
          <Text style={[styles.totalsFooterCell, styles.colMrp, styles.totalsFooterLabel]}>
            MRP
          </Text>
          <Text style={[styles.totalsFooterCell, styles.colOrdered]}> </Text>
          <Text
            style={[
              styles.totalsFooterCell,
              styles.colAvail,
              styles.totalsFooterValue,
              styles.mrpCellText,
            ]}
          >
            {`\u20B9 ${formatAmountValue(totalAvailableValue)}`}
          </Text>
          <Text
            style={[
              styles.totalsFooterCell,
              styles.colDelayed,
              styles.totalsFooterValue,
              styles.mrpCellText,
            ]}
          >
            {`\u20B9 ${formatAmountValue(totalAvailableIn7To30DaysValue)}`}
          </Text>
        </View>
      </View>
    </View>
  );
}

const BucketAvailabilityPdf: React.FC<BucketAvailabilityPdfProps> = ({
  availableRows,
  delayedRows,
  customerName,
  documentDate,
  orderNumber,
  logoSrc,
}) => {
  const combinedRows = [...availableRows, ...delayedRows];
  const totalAvailableValue = combinedRows.reduce(
    (sum, row) =>
      sum + (Number(row.mrp) || 0) * (Number(row.availableQty) || 0),
    0,
  );
  const totalAvailableIn7To30DaysValue = combinedRows.reduce(
    (sum, row) =>
      sum + (Number(row.mrp) || 0) * (Number(row.remainingQty) || 0),
    0,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.headerTable}>
          <View style={styles.headerTopRow}>
            <View style={styles.headerLeft}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {logoSrc && <Image src={logoSrc} style={styles.logo} />}

                <Text style={[styles.address, { marginLeft: 10 }]}>
                  E28 Gayatri Plaza{"\n"}
                  Transport Nagar{"\n"}
                  Jaipur Rajasthan 302004{"\n"}
                  India
                </Text>
              </View>
            </View>

            <View style={styles.headerRight}>
              <Text style={styles.title}>PARTS AVAILABILITY REPORT</Text>
            </View>
          </View>

          <View style={styles.headerBottomRow}>
            <View style={styles.metaLeft}>
              <Text style={styles.metaLine}>
                <Text style={styles.metaLabel}>Customer Name: </Text>
                <Text style={styles.metaValue}>{customerName || "—"}</Text>
              </Text>

              <Text style={styles.metaLine}>
                <Text style={styles.metaLabel}>Enquiry No: </Text>
                <Text style={styles.metaValue}>{orderNumber || "—"}</Text>
              </Text>
            </View>

            <View style={styles.metaRight}>
              <Text style={styles.metaLine}>
                <Text style={styles.metaLabel}>Date: </Text>
                <Text style={styles.metaValue}>{documentDate}</Text>
              </Text>
            </View>
          </View>
        </View>

        <CombinedPartsTable
          rows={combinedRows}
          totalAvailableValue={totalAvailableValue}
          totalAvailableIn7To30DaysValue={totalAvailableIn7To30DaysValue}
        />
      </Page>
    </Document>
  );
};

export default BucketAvailabilityPdf;
